import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Modal, Form, Select, message, Card, Typography, Tag } from 'antd';
import { PlusOutlined, DownloadOutlined, SendOutlined, StopOutlined } from '@ant-design/icons';
import { formatCurrency } from '../utils/formatters';
import { eInvoiceService, EInvoice, CreateEInvoiceData } from '../services/einvoices';

const { Title } = Typography;
const { Search } = Input;

export const EInvoicePage: React.FC = () => {
  const [eInvoices, setEInvoices] = useState<EInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [issueModalVisible, setIssueModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchEInvoices();
  }, []);

  const fetchEInvoices = async () => {
    setLoading(true);
    try {
      const response = await eInvoiceService.getAll();
      setEInvoices(response.data || []);
    } catch (error) {
      message.error('獲取電子發票列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleIssue = async (values: any) => {
    try {
      // TODO: 實現開立電子發票API
      message.success('電子發票開立成功');
      setIssueModalVisible(false);
      form.resetFields();
      fetchEInvoices();
    } catch (error) {
      message.error('電子發票開立失敗');
    }
  };

  const handleCancel = async (id: number) => {
    try {
      // TODO: 實現取消電子發票API
      message.success('電子發票已取消');
      fetchEInvoices();
    } catch (error) {
      message.error('取消電子發票失敗');
    }
  };

  const handleDownload = async (id: number) => {
    try {
      // TODO: 實現下載電子發票API
      message.success('電子發票已下載');
    } catch (error) {
      message.error('下載電子發票失敗');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: '#faad14',
      issued: '#52c41a',
      cancelled: '#f5222d',
    };
    return colors[status] || '#d9d9d9';
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      pending: '待開立',
      issued: '已開立',
      cancelled: '已取消',
    };
    return texts[status] || status;
  };

  const columns = [
    {
      title: '發票號碼',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
    },
    {
      title: '客戶名稱',
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: '總金額',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (value: number) => formatCurrency(value, 'NT$'),
    },
    {
      title: '電子發票號碼',
      dataIndex: 'e_invoice_id',
      key: 'e_invoice_id',
      render: (value: string) => value || '-',
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '開立日期',
      dataIndex: 'issue_date',
      key: 'issue_date',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: EInvoice) => (
        <Space size="middle">
          {record.status === 'pending' && (
            <Button
              type="link"
              icon={<SendOutlined />}
              onClick={() => handleIssue({ invoice_id: record.invoice_id })}
            >
              開立
            </Button>
          )}
          {record.status === 'issued' && (
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record.id)}
            >
              下載
            </Button>
          )}
          {record.status !== 'cancelled' && (
            <Button
              type="link"
              danger
              icon={<StopOutlined />}
              onClick={() => handleCancel(record.id)}
            >
              取消
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>電子發票管理</Title>
          <Space>
            <Search
              placeholder="搜索電子發票"
              style={{ width: 200 }}
              onSearch={(value) => {
                // TODO: 實現搜索功能
              }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIssueModalVisible(true)}
            >
              開立電子發票
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={eInvoices}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 項`,
          }}
        />
      </Card>

      <Modal
        title="開立電子發票"
        open={issueModalVisible}
        onCancel={() => setIssueModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleIssue}
        >
          <Form.Item
            name="invoice_id"
            label="選擇發票"
            rules={[{ required: true, message: '請選擇發票' }]}
          >
            <Select placeholder="請選擇要開立電子發票的發票">
              {/* TODO: 從API獲取發票列表 */}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                開立電子發票
              </Button>
              <Button onClick={() => setIssueModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
